import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { clientApi } from "../lib/api";
import { useSession } from "./SessionContext";

const FavoritesContext = createContext({
  codigos: new Set(),
  isFavorite: () => false,
  toggle: async () => {},
  refresh: async () => {},
  loading: false,
});

export const FavoritesProvider = ({ children }) => {
  const { customer } = useSession();
  const [codigos, setCodigos] = useState(new Set());
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!customer) return;
    setLoading(true);
    try {
      const { data } = await clientApi.get("/portal/saved-favorites");
      const items = data?.items || [];
      const missing = data?.missing || [];
      setCodigos(new Set([...items.map((i) => String(i.codigo)), ...missing.map(String)]));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [customer]);

  useEffect(() => {
    let cancelled = false;
    if (!customer) {
      Promise.resolve().then(() => {
        if (!cancelled) setCodigos(new Set());
      });
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const { data } = await clientApi.get("/portal/saved-favorites");
        if (cancelled) return;
        const items = data?.items || [];
        const missing = data?.missing || [];
        setCodigos(new Set([...items.map((i) => String(i.codigo)), ...missing.map(String)]));
      } catch {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [customer]);

  const isFavorite = useCallback(
    (codigo) => codigos.has(String(codigo)),
    [codigos]
  );

  const toggle = useCallback(async (codigo) => {
    const key = String(codigo);
    const currently = codigos.has(key);
    // Optimistic update
    const next = new Set(codigos);
    if (currently) next.delete(key);
    else next.add(key);
    setCodigos(next);
    try {
      if (currently) {
        await clientApi.delete(`/portal/saved-favorites/${encodeURIComponent(key)}`);
      } else {
        await clientApi.post(`/portal/saved-favorites/${encodeURIComponent(key)}`);
      }
    } catch {
      // Revert on error
      setCodigos(codigos);
      throw new Error("No se pudo actualizar favorito");
    }
  }, [codigos]);

  return (
    <FavoritesContext.Provider value={{ codigos, isFavorite, toggle, refresh, loading }}>
      {children}
    </FavoritesContext.Provider>
  );
};

export const useFavorites = () => useContext(FavoritesContext);
